import { useCallback, useState } from 'react';

type ModalStateUpdater = (previous: boolean) => boolean;

type ModalStateOptions = {
  /** When true, the modal starts open. Defaults to false. */
  defaultOpen?: boolean;
  /** Optional side effect fired whenever the open state toggles. */
  onOpenChange?: (open: boolean) => void;
};

type UseModalStateReturn = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setOpen: (next: boolean | ModalStateUpdater) => void;
};

export function useModalState(options?: ModalStateOptions): UseModalStateReturn {
  const { defaultOpen = false, onOpenChange } = options ?? {};
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  const setOpen = useCallback(
    (next: boolean | ModalStateUpdater) => {
      setIsOpen((previous) => {
        const nextValue = typeof next === 'function' ? (next as ModalStateUpdater)(previous) : next;
        if (nextValue !== previous) {
          onOpenChange?.(nextValue);
        }
        return nextValue;
      });
    },
    [onOpenChange]
  );

  const open = useCallback(() => setOpen(true), [setOpen]);
  const close = useCallback(() => setOpen(false), [setOpen]);
  const toggle = useCallback(() => setOpen((previous) => !previous), [setOpen]);

  return {
    isOpen,
    open,
    close,
    toggle,
    setOpen,
  };
}

export type { ModalStateOptions, UseModalStateReturn };
